import cx from 'clsx';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as FilterIcon } from '@assets/svg/filter.svg';
import { ReactComponent as SearchIcon } from '@assets/svg/ic-search.svg';
import { ReactComponent as StatisticIcon } from '@assets/svg/statistic.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import ResetButton from '@components/common/ResetButton/ResetButton';
import Slider from '@components/common/Slider/Slider';
import DatePicker from '@components/form/DatePicker/DatePicker';
import FilterSearch from '@components/uikit/FilterSearch/FilterSearch';
import Select from '@components/uikit/Select/Select';

import { getAITypesList } from '@helpers/getAITypesList';

import useTranslation from '@hooks/useTranslation';

import type { ISelectOption } from '@model/select';

import { archiveActionItemsTooltipIds } from '@pages/Archive/constants/archiveActionItemsTooltipIds';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue, selectAppRtl } from '@store/useAppStore';

import { getFilterCauseCategoriesList } from '../../helpers/getFilterCauseCategoriesList';
import { parseDateOrNull } from '../../helpers/parseDateOrNull';
import type { IArchivedAiFilterData, IArchivedAiStats, IArchivedAiStore } from '../../model/archivedAi';
import { selectArchiveFiltersData, useArchiveFiltersActions } from '../../store/useArchiveFiltersStore';
import AdditionalFilters from '../AdditionalFilters/AdditionalFilters';
import ArchiveStatistic from '../ArchiveStatistic/ArchiveStatistic';
import style from './style.module.scss';

interface IProps {
  filterData: IArchivedAiFilterData | undefined;
  statData: IArchivedAiStats | undefined;
  isLoading: boolean;
}

const ArchiveFilter: React.FC<IProps> = ({ filterData, statData, isLoading }) => {
  const locationSelectValue = selectAppLocationSelectValue();
  const { customerId } = useParams();

  const {
    causeCategory: filterCauseCategory,
    timeUpdatedRange: { minTimeUpdated: filterMinTimeUpdated, maxTimeUpdated: filterMaxTimeUpdated },
    timeCreatedReportRange: { minTimeCreated: filterMinTimeCreated, maxTimeCreated: filterMaxTimeCreated },
    costAvoidedRange: { minCostAvoided: filterMinCostAvoided, maxCostAvoided: filterMaxCostAvoided },
    searchValue: filterSearchValue,
    type: filterType,
  }: IArchivedAiStore = selectArchiveFiltersData();

  const [activeExtraPanel, setActiveExtraPanel] = useState<'statistic' | 'search'>(
    filterSearchValue ? 'search' : 'statistic',
  );
  const [showAdditionalFilter, setShowAdditionalFilter] = useState(false);

  const { t } = useTranslation();
  const { setArchiveFiltersKey, setArchiveFiltersInitial } = useArchiveFiltersActions();

  const rtl = selectAppRtl();

  const { data: profileData } = useUserProfileQuery();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });
  const { data: assetTreeData } = useAssetTreeQuery();

  const resetFilter = () => {
    setArchiveFiltersInitial();
  };

  const filterCauseCategoriesList = getFilterCauseCategoriesList(t);
  const typesList = getAITypesList(t);

  const minTimeUpdated = parseDateOrNull(filterData?.minTimeUpdated);

  const showClearFilterBtn =
    filterType.value !== 'all' ||
    filterMinCostAvoided !== undefined ||
    filterMaxTimeUpdated !== undefined ||
    filterMinCostAvoided !== undefined ||
    filterMaxCostAvoided !== undefined ||
    filterCauseCategory.value !== 'all' ||
    filterMinTimeCreated !== undefined ||
    filterMaxTimeCreated !== undefined;

  const showExtraFiltersIcon = filterMinTimeCreated !== undefined || filterMaxTimeCreated !== undefined;
  const showSmallWidthExtraFiltersIcon =
    filterMinTimeUpdated !== undefined ||
    filterMaxTimeUpdated !== undefined ||
    filterMinCostAvoided !== undefined ||
    filterMaxCostAvoided !== undefined ||
    showExtraFiltersIcon;

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  return (
    <div>
      <div className={style.filterPanel}>
        <div className={style.filtersBlock}>
          <div className={style.selectField}>
            <Select
              label={t('Cause Category')}
              value={
                filterCauseCategory?.value === 'all' ? filterCauseCategoriesList[0] : filterCauseCategory
              }
              onChange={(option: ISelectOption) => setArchiveFiltersKey('causeCategory', option)}
              options={filterCauseCategoriesList}
              itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
              type={'woHelperText'}
              dataQa={'causeCategory'}
            />
          </div>
          <div className={style.selectField}>
            <Select
              label={t('Action Item Type')}
              options={typesList}
              value={filterType.value === 'all' ? typesList[0] : filterType}
              itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
              onChange={(option: ISelectOption) => setArchiveFiltersKey('type', option)}
              type={'woHelperText'}
              dataQa={'type'}
            />
          </div>
          <div className={style.extraFilters}>
            <div className={style.datepickerRange}>
              <DatePicker
                label={t('Last Update Date')}
                id="daterange-last-update-facilities"
                placeholderText={t('Select Date Range')}
                dateFormat={rtl ? 'yyyy/MM/dd' : `${profileData?.dateFormat.date}`}
                startDate={filterMinTimeUpdated ? new Date(filterMinTimeUpdated) : undefined}
                endDate={filterMaxTimeUpdated ? new Date(filterMaxTimeUpdated) : undefined}
                minDate={minTimeUpdated as Date}
                onChange={(values: [Date | null, Date | null]) =>
                  setArchiveFiltersKey('timeUpdatedRange', {
                    minTimeUpdated: values[0] ? format(values[0], 'MM/dd/yyyy') : undefined,
                    maxTimeUpdated: values[1] ? format(values[1], 'MM/dd/yyyy') : undefined,
                  })
                }
                selectsRange
                showMonthDropdown
                showYearDropdown
                type="woHelperText"
              />
            </div>
            {customerInfoData?.showTotalCostSavings && (
              <div className={style.averageTime}>
                <Slider
                  label={t('Cost saving range')}
                  from={filterMinCostAvoided || filterData?.minCostAvoided || 0}
                  to={filterMaxCostAvoided || filterData?.maxCostAvoided || 0}
                  onChange={([left, right]) => {
                    setArchiveFiltersKey('costAvoidedRange', {
                      minCostAvoided: left,
                      maxCostAvoided: right,
                    });
                  }}
                  min={filterData?.minCostAvoided}
                  max={filterData?.maxCostAvoided}
                  startInput={<span className={style.startAdornment}>{currentCurrency.symbol}</span>}
                />
              </div>
            )}
          </div>

          <div
            className={style.additionalFiltersMenu}
            data-tooltip-id="archive-extra-filters"
            onClick={() => setShowAdditionalFilter(true)}
          >
            <div
              className={cx(style.extraFiltersIconContainer, {
                [style.activeExtraFiltersIconContainer]: showAdditionalFilter,
              })}
              data-tooltip-content={t('View Additional Filters')}
              data-tooltip-id={archiveActionItemsTooltipIds.LONG_TEXT_TOOLTIP_ID}
            >
              <span
                className={cx({
                  [style.activeExtraFiltersIconDot]: showExtraFiltersIcon,
                  [style.activeSmallWidthExtraFiltersIconDot]: showSmallWidthExtraFiltersIcon,
                })}
              >
                <FilterIcon className={style.filterIcon} />
              </span>
            </div>
          </div>
          <div className={style.resetWrapper}>
            <ResetButton showResetButton={showClearFilterBtn} onClick={resetFilter} />
          </div>
        </div>
        <div className={style.iconsBlock}>
          <div
            className={cx(style.iconWrapper, { [style.activeIcon]: activeExtraPanel === 'statistic' })}
            onClick={() => setActiveExtraPanel('statistic')}
            data-tooltip-id="archive-ai-filter"
            data-tooltip-content={t('General Statistics Panel')}
          >
            <StatisticIcon className={style.statisticIcon} />
          </div>
          <div
            className={cx(style.iconWrapper, { [style.activeIcon]: activeExtraPanel === 'search' })}
            onClick={() => setActiveExtraPanel('search')}
            data-tooltip-id="archive-ai-filter"
            data-tooltip-content={t('Search Bar')}
          >
            <SearchIcon className={style.searchIcon} />
          </div>
        </div>
      </div>
      <div className={style.extraPanel}>
        {activeExtraPanel === 'statistic' ? (
          <ArchiveStatistic
            totalCostAvoided={
              isSelectedAllFacility
                ? statData?.costAvoidedCustomerCurrency
                : statData?.costAvoidedFacilityCurrency
            }
            equipmentLaborSaving={
              isSelectedAllFacility
                ? statData?.equipmentLaborSavingsCustomerCurrency
                : statData?.equipmentLaborSavingsFacilityCurrency
            }
            totalDowntimeAvoided={statData?.downtimeAvoided}
            isLoading={isLoading}
          />
        ) : (
          <div className={style.archiveSearch}>
            <FilterSearch
              value={filterSearchValue}
              onChange={(value) => setArchiveFiltersKey('searchValue', value || '')}
              variant={'header-search'}
            />
          </div>
        )}
      </div>
      <AdditionalFilters
        id="archive-extra-filters"
        visible={showAdditionalFilter}
        setIsOpen={setShowAdditionalFilter}
        filterData={filterData}
      />
      <Tooltip className={style.aiTableTooltip} id="archive-ai-filter" />
    </div>
  );
};

export default ArchiveFilter;
